import React, { useEffect, useState } from "react";
import BlockUi from "react-block-ui";
import { IoArrowBackCircleOutline } from "react-icons/io5";
import { toast } from "react-toastify";
import MyYuukke from "../../../api/MyYuukke";
import { useDispatch } from "react-redux";
import { getUserSlots } from "../../../redux/actions/calendarAction";
import Select from "react-select";
import { isValidUPI } from "../../../utils";
import { dBody, dropFlowStyle } from "../../../components/Utils";

// ─── CONFIG ───────────────────────────────────────────────────────────────────
const PAYMENT_METHODS = [
  { label: "Account Number", value: "accountNumber", icon: "🏦" },
  { label: "PayPal ID",      value: "paypalId",      icon: "🅿️" },
  { label: "UPI ID",         value: "upiId",         icon: "📱" },
];

const ACCOUNT_TYPES = [
  { label: "Saving Account",  value: "saving"  },
  { label: "Current Account", value: "current" },
];

// ─── RESPONSIVE STYLES ────────────────────────────────────────────────────────
// Injected once; scoped under .bd-wrapper so it can't leak into the rest of the app.
const ResponsiveStyles = () => (
  <style>{`
    .bd-wrapper {
      max-width: 1200px;
      margin: 0 auto;
      padding: 1.5rem 1rem 4rem;
      font-family: "Inter", system-ui, sans-serif;
      box-sizing: border-box;
    }
    .bd-wrapper *, .bd-wrapper *::before, .bd-wrapper *::after {
      box-sizing: border-box;
    }

    .bd-section-group { margin-bottom: 2.5rem; }

    .bd-group-title {
      display: flex;
      align-items: center;
      gap: 10px;
      font-size: 0.88rem;
      font-weight: 500;
      color: #a00030;
      text-transform: uppercase;
      letter-spacing: 0.09em;
      margin-bottom: 1.5rem;
      padding-bottom: 0.6rem;
      border-bottom: 1.5px solid #f3cdd5;
    }
    .bd-group-badge {
      width: 22px;
      height: 22px;
      border-radius: 50%;
      background: #fdf2f4;
      border: 1px solid #f3cdd5;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      font-size: 0.7rem;
      font-weight: 500;
      color: #a00030;
      flex-shrink: 0;
    }

    /* 2-column grid on desktop, single column on mobile */
    .bd-grid2 {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 1.25rem 1.5rem;
    }
    .bd-grid-full { grid-column: 1 / -1; }

    .bd-field {
      display: flex;
      flex-direction: column;
      gap: 8px;
      min-width: 0; /* prevents grid children from overflowing on small screens */
    }
    .bd-label {
      font-size: 1rem;
      font-weight: 500;
      color: #1f2937;
      margin: 0;
      display: flex;
      align-items: center;
      gap: 4px;
    }
    .bd-required { color: #dc2626; font-size: 0.85rem; }

    .bd-input {
      height: 50px;
      border: 1.5px solid #d1d5db;
      border-radius: 8px;
      padding: 0 16px;
      font-size: 1rem;
      color: #111;
      background: #fff;
      outline: none;
      width: 100%;
      font-family: "Inter", system-ui, sans-serif;
      transition: border-color 0.15s, box-shadow 0.15s;
    }
    .bd-input:focus {
      border-color: #a00030;
      box-shadow: 0 0 0 3px rgba(160, 0, 48, 0.12);
    }

    /* Payment method cards: 3 across on desktop, stack to 1 col on small phones */
    .bd-method-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 0.85rem;
      margin-bottom: 1.75rem;
    }
    .bd-method-card {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 8px;
      padding: 18px 12px;
      border: 1.5px solid #d1d5db;
      border-radius: 8px;
      background: #fff;
      cursor: pointer;
      transition: all 0.15s ease;
      text-align: center;
      -webkit-tap-highlight-color: transparent;
      min-height: 88px; /* comfortable touch target */
    }
    .bd-method-card:hover { border-color: #a00030; }
    .bd-method-card.active {
      border-color: #a00030;
      background: #fdf2f4;
    }
    .bd-method-icon { font-size: 26px; line-height: 1; }
    .bd-method-label {
      font-size: 0.9rem;
      font-weight: 500;
      color: #374151;
      margin: 0;
    }
    .bd-method-label.active { color: #a00030; }
    .bd-method-check { font-size: 0.75rem; color: #a00030; font-weight: 500; }

    .bd-info-pill {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 7px 14px;
      background: #f0f9ff;
      border: 1px solid #bae6fd;
      border-radius: 20px;
      font-size: 0.85rem;
      font-weight: 500;
      color: #0369a1;
      margin-bottom: 1rem;
      max-width: 100%;
    }

    .bd-security-note {
      font-size: 0.85rem;
      color: #9ca3af;
      margin: 8px 0 0;
      display: flex;
      align-items: center;
      gap: 6px;
    }

    .bd-divider {
      border: none;
      border-top: 1.5px dashed #e5e7eb;
      margin: 0.5rem 0 2.5rem;
    }

    .bd-footer {
      display: flex;
      justify-content: flex-end;
      padding-top: 1.5rem;
      border-top: 1.5px dashed #e5e7eb;
      margin-top: 1rem;
    }
    .bd-btn-submit {
      background: #a00030;
      border: none;
      color: white;
      padding: 14px 32px;
      border-radius: 8px;
      font-size: 1rem;
      font-weight: 500;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
      font-family: "Inter", system-ui, sans-serif;
      width: 100%;
      transition: background 0.15s;
    }
    .bd-btn-submit:hover { background: #87002a; }
    .bd-btn-submit:focus-visible {
      outline: 2px solid #a00030;
      outline-offset: 2px;
    }

    .bd-header {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.9rem 1rem;
      margin: 0.5rem 0 0.5rem;
      border-bottom: 1px solid #80808063;
      font-size: 1.15rem;
      font-weight: 600;
    }
    .bd-back-icon {
      width: 1.9rem;
      height: 1.9rem;
      flex-shrink: 0;
      cursor: pointer;
      color: #374151;
    }

    /* ── Tablet ── */
    @media (max-width: 768px) {
      .bd-wrapper { padding: 1.25rem 0.9rem 3rem; }
      .bd-method-grid { gap: 0.6rem; }
      .bd-btn-submit { width: 100%; }
      .bd-footer { justify-content: stretch; }
    }

    /* ── Mobile ── */
    @media (max-width: 560px) {
      .bd-grid2 { grid-template-columns: 1fr; gap: 1rem; }
      .bd-method-grid { grid-template-columns: repeat(3, 1fr); gap: 0.5rem; }
      .bd-method-card { padding: 12px 6px; min-height: 78px; }
      .bd-method-icon { font-size: 22px; }
      .bd-method-label { font-size: 0.78rem; }
      .bd-method-check { font-size: 0.68rem; }
      .bd-input { height: 48px; font-size: 0.95rem; }
      .bd-group-title { font-size: 0.8rem; margin-bottom: 1.1rem; }
      .bd-section-group { margin-bottom: 2rem; }
      .bd-header { font-size: 1.02rem; padding: 0.75rem 0.75rem; }
    }

    /* ── Very small phones ── */
    @media (max-width: 360px) {
      .bd-method-grid { grid-template-columns: 1fr; }
      .bd-method-card { flex-direction: row; justify-content: flex-start; min-height: 56px; padding: 10px 14px; }
    }
  `}</style>
);

// ─── HELPERS ──────────────────────────────────────────────────────────────────
const Req = () => <span className="bd-required">*</span>;

const GroupTitle = ({ num, children }) => (
  <div className="bd-group-title">
    <span className="bd-group-badge">{num}</span>
    {children}
  </div>
);

const Field = ({ label, required, children, className = "" }) => (
  <div className={`bd-field ${className}`}>
    <label className="bd-label">
      {label} {required && <Req />}
    </label>
    {children}
  </div>
);

const Divider = () => <hr className="bd-divider" />;

// ─── COMPONENT ────────────────────────────────────────────────────────────────
const BankDetails = ({ onBack, userSlots }) => {
  const dispatch = useDispatch();

  const [state, setState] = useState({
    bankId:       userSlots?.accountNumber ?? "",
    type:         PAYMENT_METHODS[0],
    accountValue: ACCOUNT_TYPES[1],
    panCard:      userSlots?.panCard   ?? "",
    ifseCode:     userSlots?.ifseCode  ?? "",
    branchName:   userSlots?.branchName ?? "",
    loader:       false,
  });

  useEffect(() => {
    if (userSlots?.bankDetailsType) {
      const found = PAYMENT_METHODS.find((m) => m.value === userSlots.bankDetailsType);
      if (found) setState((p) => ({ ...p, type: found }));
    }
    if (userSlots?.accountType) {
      const found = ACCOUNT_TYPES.find((a) => a.value === userSlots.accountType);
      if (found) setState((p) => ({ ...p, accountValue: found }));
    }
  }, [userSlots]);

  const updateState = (name, value) => setState((p) => ({ ...p, [name]: value }));

  const isAccountNumber = state.type.value === "accountNumber";

 const updateDetails = (e) => {
    e.preventDefault();
    if (state.type.value === "upiId" && !isValidUPI(state.bankId)) {
      toast.error("Invalid UPI ID");
      return;
    }
    
    // Check if the current selection is a direct bank account
    const isAccount = state.type.value === "accountNumber";

    const data = {
      accountNumber: state.bankId,
      type:          state.type.value,
      panCard:       state.panCard,
      
      // FIX: If it's an account number, send the values. 
      // If it's UPI or PayPal, force them to be empty strings to clear the DB.
      ifseCode:    isAccount ? (state.ifseCode || "") : "",
      accountType: isAccount ? (state.accountValue?.value || "") : "",
      branchName:  isAccount ? (state.branchName || "") : "",
    };

    setState((p) => ({ ...p, loader: true }));
    MyYuukke.updateDetails(data)
      .then(() => {
        dispatch(getUserSlots());
        toast.success("Updated successfully");
        onBack("bank", true);
      })
      .catch(() => toast.error("Something went wrong!"))
      .finally(() => setState((p) => ({ ...p, loader: false })));
  };

  return (
    <BlockUi
      tag="div"
      message="Loading, please wait"
      style={{ minHeight: "134px" }}
      className="wallLoadingPage"
      blocking={state.loader}
    >
      <ResponsiveStyles />

      {/* Page Header */}
      <div className="bd-header">
        <IoArrowBackCircleOutline
          onClick={() => onBack()}
          className="bd-back-icon"
          aria-label="Go back"
          role="button"
        />
        Bank Account Details
      </div>

      <div className="bd-wrapper">
        <form onSubmit={updateDetails}>

          {/* ══ 1 · Payment Method ══ */}
          <div className="bd-section-group">
            <GroupTitle num="1">Payment Method</GroupTitle>

            <div className="bd-method-grid">
              {PAYMENT_METHODS.map((method) => {
                const isActive = state.type.value === method.value;
                return (
                  <div
                    key={method.value}
                    className={`bd-method-card ${isActive ? "active" : ""}`}
                    onClick={() =>
                      setState((p) => ({ ...p, type: method, bankId: "", ifseCode: "" }))
                    }
                    role="button"
                    tabIndex={0}
                    aria-pressed={isActive}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setState((p) => ({ ...p, type: method, bankId: "", ifseCode: "" }));
                      }
                    }}
                  >
                    <span className="bd-method-icon">{method.icon}</span>
                    <span className={`bd-method-label ${isActive ? "active" : ""}`}>
                      {method.label}
                    </span>
                    {isActive && <span className="bd-method-check">✓ Selected</span>}
                  </div>
                );
              })}
            </div>

            <div className="bd-info-pill">
              <span>{state.type.icon}</span>
              Entering: {state.type.label}
            </div>

            <Field label={`Enter your ${state.type.label}`} required>
              <input
                className="bd-input"
                type="text"
                name="bankId"
                placeholder={
                  state.type.value === "upiId"
                    ? "yourname@upi"
                    : state.type.value === "paypalId"
                    ? "yourname@paypal.com"
                    : "Enter account number"
                }
                value={state.bankId}
                onChange={(e) => updateState("bankId", e.target.value)}
                required
              />
            </Field>
          </div>

          {/* ══ 2 · Bank Details (conditional) ══ */}
          {isAccountNumber && (
            <>
              <Divider />
              <div className="bd-section-group">
                <GroupTitle num="2">Bank Details</GroupTitle>
                <div className="bd-grid2">

                  <Field label="Account Type" required>
                    <Select
                      options={ACCOUNT_TYPES}
                      value={state.accountValue}
                      onChange={(e) => updateState("accountValue", e)}
                      required
                      menuPortalTarget={dBody}
                      styles={dropFlowStyle}
                    />
                  </Field>

                  <Field label="IFSC Code" required>
                    <input
                      className="bd-input"
                      type="text"
                      placeholder="e.g. SBIN0001234"
                      value={state.ifseCode}
                      onChange={(e) => updateState("ifseCode", e.target.value)}
                      required
                    />
                  </Field>

                  <Field label="Branch Name" required className="bd-grid-full">
                    <input
                      className="bd-input"
                      type="text"
                      placeholder="e.g. Koramangala Branch"
                      value={state.branchName}
                      onChange={(e) => updateState("branchName", e.target.value)}
                      required
                    />
                  </Field>

                </div>
              </div>
            </>
          )}

          <Divider />

          {/* ══ Tax Information ══ */}
          <div className="bd-section-group">
            <GroupTitle num={isAccountNumber ? "3" : "2"}>Tax Information</GroupTitle>

            <Field label="PAN Card Number" required>
              <input
                className="bd-input"
                type="text"
                name="panCard"
                placeholder="e.g. ABCDE1234F"
                value={state.panCard}
                onChange={(e) => updateState("panCard", e.target.value.toUpperCase())}
                required
                maxLength={12}
              />
            </Field>
            <p className="bd-security-note">
              🔒 Your financial details are encrypted and never shared
            </p>
          </div>

          {/* ── Footer ── */}
          <div className="bd-footer">
            <button type="submit" className="bd-btn-submit">
              ✓ Update Details
            </button>
          </div>

        </form>
      </div>
    </BlockUi>
  );
};

export default BankDetails;